import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { css } from '@emotion/react';

import { IWizardFile } from '../models/interfaces/IWizardFile';
import { QueueType } from '../models/enums/QueueType';
import { UploadType } from '../models/enums/UploadType';
import { UPLOAD_TYPE_META } from '../uploadType/UploadTypeRegistry';
import { QueueProcessingLocalUpload } from './QueueProcessingLocalUpload';
import { QueueProcessingSftpPath } from './QueueProcessingSftpPath';
import { QueueProcessingServerUpload } from './QueueProcessingServerUpload';

const containerStyle = css({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxSizing: 'border-box',
    padding: 24,
    gap: 16,
    '& .upload-method-section': {
        flexShrink: 0,
    },
    '& .upload-method-cards': {
        display: 'flex',
        gap: 12,
    },
    '& .upload-method-card': {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '12px 16px',
        borderRadius: 10,
        border: '2px solid #e0e0e0',
        backgroundColor: '#ffffff',
        cursor: 'pointer',
        transition: 'border-color 0.15s ease, background-color 0.15s ease, box-shadow 0.15s ease',
        userSelect: 'none',
        '&:hover': {
            borderColor: '#90caf9',
            backgroundColor: '#f5f9ff',
        },
    },
    '& .upload-method-card--active': {
        borderColor: '#1565c0',
        backgroundColor: '#e3f2fd',
        boxShadow: '0 0 0 3px rgba(21,101,192,0.12)',
        '&:hover': {
            borderColor: '#1565c0',
            backgroundColor: '#e3f2fd',
        },
    },
    '& .upload-method-card-icon': {
        flexShrink: 0,
        width: 40,
        height: 40,
        borderRadius: 8,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    '& .upload-method-card-text': {
        flex: 1,
        minWidth: 0,
    },
    '& .upload-content': {
        flex: 1,
        minHeight: 0,
    },
});

interface IProps {
    availableTypes: UploadType[];
    queueType: QueueType;
    onFilesChange?: (files: IWizardFile[]) => void;
    onUploadTypeChange?: (type: UploadType) => void;
}

export function QueueProcessingUpload({ availableTypes, queueType, onFilesChange, onUploadTypeChange }: IProps) {
    const [activeType, setActiveType] = useState<UploadType>(availableTypes[0]);

    useEffect(() => {
        onUploadTypeChange?.(availableTypes[0]);
    }, []);

    function handleTypeChange(type: UploadType) {
        setActiveType(type);
        onUploadTypeChange?.(type);
    }

    return (
        <Box css={containerStyle}>
            <Box className='upload-method-section'>
                <Box className='upload-method-cards'>
                    {availableTypes.map((type) => {
                        const { label, description, Icon } = UPLOAD_TYPE_META[type];
                        const isActive = activeType === type;

                        return (
                            <Box
                                key={type}
                                className={`upload-method-card${isActive ? ' upload-method-card--active' : ''}`}
                                onClick={() => handleTypeChange(type)}
                            >
                                <Box
                                    className='upload-method-card-icon'
                                    sx={{
                                        backgroundColor: isActive ? '#1565c020' : '#f5f5f5',
                                    }}
                                >
                                    <Icon sx={{ fontSize: 22, color: isActive ? '#1565c0' : '#757575' }} />
                                </Box>
                                <Box className='upload-method-card-text'>
                                    <Typography
                                        variant='body2'
                                        sx={{ fontWeight: 700, color: isActive ? '#1565c0' : '#212121', lineHeight: 1.3 }}
                                    >
                                        {label}
                                    </Typography>
                                    <Typography variant='caption' sx={{ color: '#9e9e9e', lineHeight: 1.3 }}>
                                        {description}
                                    </Typography>
                                </Box>
                            </Box>
                        );
                    })}
                </Box>
            </Box>

            <Box className='upload-content'>
                {activeType === UploadType.LocalUpload && (
                    <QueueProcessingLocalUpload />
                )}
                {activeType === UploadType.ServerFileUpload && (
                    <QueueProcessingServerUpload />
                )}
                {activeType === UploadType.S3 && (
                    <QueueProcessingSftpPath queueType={queueType} onFilesChange={onFilesChange} />
                )}
            </Box>
        </Box>
    );
}
